import { Calendar, DateField, DatePicker, Label } from '@heroui/react';
import type React from 'react';
import { I18nProvider } from 'react-aria-components/I18nProvider';
import { getNodeTestIdProp, usePickerLocale } from '@/utils';
import {
    formatDatePickerValue,
    parseDatePickerValue,
    type DatePickerOutputFormat,
} from './datePickerFieldValue';

type DatePickerFieldProps = {
    error?: string;
    id: string;
    isDisabled?: boolean;
    isRequired?: boolean;
    label: string;
    granularity?: 'day' | 'minute';
    /**
     * Earliest selectable date, in any format `parseDatePickerValue` accepts.
     *
     * Passed through to the calendar so out-of-range days are unselectable
     * rather than merely rejected after the fact — a constraint the user can
     * see beats one they discover by being told no.
     */
    minValue?: string;
    /** Latest selectable date. Same handling as {@link minValue}. */
    maxValue?: string;
    name: string;
    onChange: (value: string) => void;
    outputFormat?: DatePickerOutputFormat;
    value: string;
};

export const DatePickerField: React.FC<DatePickerFieldProps> = ({
    error,
    id,
    isDisabled,
    isRequired,
    label,
    granularity = 'day',
    minValue,
    maxValue,
    name,
    onChange,
    outputFormat = 'YYYY-MM-DD',
    value,
}) => {
    const pickerValue = parseDatePickerValue(value, outputFormat);
    const minDateValue = minValue
        ? parseDatePickerValue(minValue, outputFormat)
        : null;
    const maxDateValue = maxValue
        ? parseDatePickerValue(maxValue, outputFormat)
        : null;
    let segmentIndex = 0;
    // Segment order follows the active UI language (`en-GB` / `ka-GE`), so
    // every picker in the app reads DD/MM/YYYY — see `usePickerLocale`.
    const locale = usePickerLocale();

    return (
        <I18nProvider locale={locale}>
            <DatePicker
                className="w-full"
                granularity={granularity}
                isDisabled={isDisabled}
                isInvalid={Boolean(error)}
                isRequired={isRequired}
                {...(minDateValue ? { minValue: minDateValue } : {})}
                {...(maxDateValue ? { maxValue: maxDateValue } : {})}
                name={name}
                onChange={(nextValue) =>
                    onChange(formatDatePickerValue(nextValue, outputFormat))
                }
                value={pickerValue}
                {...getNodeTestIdProp(id)}
            >
                <Label id={`${id}-label`} {...getNodeTestIdProp(`${id}-label`)}>
                    {label}
                </Label>
                <DateField.Group
                    fullWidth
                    variant="secondary"
                    {...getNodeTestIdProp(`${id}-group`)}
                >
                    <DateField.Input {...getNodeTestIdProp(`${id}-input`)}>
                        {(segment) => {
                            const currentSegmentIndex = segmentIndex;
                            segmentIndex += 1;

                            return (
                                <DateField.Segment
                                    segment={segment}
                                    {...getNodeTestIdProp(
                                        `${id}-segment-${currentSegmentIndex}-${segment.type}`,
                                    )}
                                />
                            );
                        }}
                    </DateField.Input>
                    <DateField.Suffix {...getNodeTestIdProp(`${id}-suffix`)}>
                        <DatePicker.Trigger
                            {...getNodeTestIdProp(`${id}-trigger`)}
                        >
                            <DatePicker.TriggerIndicator
                                {...getNodeTestIdProp(
                                    `${id}-trigger-indicator`,
                                )}
                            />
                        </DatePicker.Trigger>
                    </DateField.Suffix>
                </DateField.Group>
                {error ? (
                    <p
                        className="text-sm text-danger"
                        {...getNodeTestIdProp(`${id}-error`)}
                    >
                        {error}
                    </p>
                ) : null}
                <DatePicker.Popover {...getNodeTestIdProp(`${id}-popover`)}>
                    <Calendar
                        aria-label={label}
                        {...getNodeTestIdProp(`${id}-calendar`)}
                    >
                        <Calendar.Header
                            {...getNodeTestIdProp(`${id}-calendar-header`)}
                        >
                            <Calendar.YearPickerTrigger
                                {...getNodeTestIdProp(
                                    `${id}-calendar-year-trigger`,
                                )}
                            >
                                <Calendar.YearPickerTriggerHeading
                                    {...getNodeTestIdProp(
                                        `${id}-calendar-year-heading`,
                                    )}
                                />
                                <Calendar.YearPickerTriggerIndicator
                                    {...getNodeTestIdProp(
                                        `${id}-calendar-year-indicator`,
                                    )}
                                />
                            </Calendar.YearPickerTrigger>
                            <Calendar.NavButton
                                slot="previous"
                                {...getNodeTestIdProp(
                                    `${id}-calendar-previous`,
                                )}
                            />
                            <Calendar.NavButton
                                slot="next"
                                {...getNodeTestIdProp(`${id}-calendar-next`)}
                            />
                        </Calendar.Header>
                        <Calendar.Grid
                            {...getNodeTestIdProp(`${id}-calendar-grid`)}
                        >
                            <Calendar.GridHeader
                                {...getNodeTestIdProp(
                                    `${id}-calendar-grid-header`,
                                )}
                            >
                                {(day) => (
                                    <Calendar.HeaderCell
                                        {...getNodeTestIdProp(
                                            `${id}-calendar-header-cell-${day.toLowerCase()}`,
                                        )}
                                    >
                                        {day}
                                    </Calendar.HeaderCell>
                                )}
                            </Calendar.GridHeader>
                            <Calendar.GridBody
                                {...getNodeTestIdProp(
                                    `${id}-calendar-grid-body`,
                                )}
                            >
                                {(date) => (
                                    <Calendar.Cell
                                        date={date}
                                        {...getNodeTestIdProp(
                                            `${id}-calendar-cell-${date}`,
                                        )}
                                    />
                                )}
                            </Calendar.GridBody>
                        </Calendar.Grid>
                        <Calendar.YearPickerGrid
                            {...getNodeTestIdProp(`${id}-calendar-year-grid`)}
                        >
                            <Calendar.YearPickerGridBody
                                {...getNodeTestIdProp(
                                    `${id}-calendar-year-grid-body`,
                                )}
                            >
                                {({ year }) => (
                                    <Calendar.YearPickerCell
                                        year={year}
                                        {...getNodeTestIdProp(
                                            `${id}-calendar-year-cell-${year}`,
                                        )}
                                    />
                                )}
                            </Calendar.YearPickerGridBody>
                        </Calendar.YearPickerGrid>
                    </Calendar>
                </DatePicker.Popover>
            </DatePicker>
        </I18nProvider>
    );
};
